import { useDispatch } from 'react-redux';

import axios from 'axios';

import { HttpStatusCode } from '@framework/constants/enums/http';
import useTranslation from '@framework/hooks/useTranslation';
import { splitNotificationErrorMessage } from '@framework/utils';

import { sendSnackbar } from '@redux/reducers/snackbars.duck';

import { getErrorMessageByCode } from '@accounts/constants/authentication';
import {
    getLoginErrorCode,
    getThrottleAction,
    type ThrottleErrorResponse
} from '@accounts/utils/authentication.utils';

import { SnackbarType } from 'styled-system';

import { type ApiErrorResponse } from '@type/api';

import type { AxiosError } from 'axios';

type ErrorCbType = {
    defaultCb?: (error: AxiosError<ApiErrorResponse>) => void;
    invalidRequestCb?: (error: AxiosError<ApiErrorResponse>) => void;
    unAuthorizedCb?: (error: AxiosError<ApiErrorResponse>) => void;
    notFoundCb?: (error: AxiosError<ApiErrorResponse>) => void;
    unprocessableCb?: (error: AxiosError<ApiErrorResponse>) => void;
    repeatedRequestsCb?: (error: AxiosError<ApiErrorResponse>) => void;
};

const useLoginError = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const handleBadRequest = (error: AxiosError<ApiErrorResponse>) => {
        const { header, message } = splitNotificationErrorMessage(
            getErrorMessageByCode({ code: getLoginErrorCode(error)?.code as string })
        );
        dispatch(
            sendSnackbar({
                header,
                message,
                type: SnackbarType.Error
            })
        );
    };

    const handleRepeatedRequests = (error: unknown) => {
        const errorAction = getThrottleAction(error as AxiosError<ThrottleErrorResponse>);
        const { header, message } = splitNotificationErrorMessage(
            getErrorMessageByCode(errorAction)
        );
        dispatch(
            sendSnackbar({
                header,
                message,
                type: SnackbarType.Error
            })
        );
    };

    const handleDefaultError = () => {
        dispatch(
            sendSnackbar({
                header: t('Something went wrong'),
                message: t('Please try again'),
                type: SnackbarType.Error
            })
        );
    };

    const handleLoginError = (
        error: unknown,
        {
            defaultCb,
            invalidRequestCb,
            unAuthorizedCb,
            notFoundCb,
            unprocessableCb,
            repeatedRequestsCb
        }: ErrorCbType = {}
    ) => {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            switch (status) {
                case HttpStatusCode.BadRequest:
                    handleBadRequest(error);
                    if (invalidRequestCb) {
                        invalidRequestCb(error);
                    }
                    break;
                case HttpStatusCode.Unauthorized:
                    if (unAuthorizedCb) {
                        unAuthorizedCb(error);
                    }
                    break;
                case HttpStatusCode.NotFound:
                    if (notFoundCb) {
                        notFoundCb(error);
                    }
                    break;
                case HttpStatusCode.Unprocessable:
                    if (unprocessableCb) {
                        unprocessableCb(error);
                    }
                    break;
                case HttpStatusCode.TooManyRequests:
                    handleRepeatedRequests(error);
                    if (repeatedRequestsCb) {
                        repeatedRequestsCb(error);
                    }
                    break;
                default:
                    handleDefaultError();
                    if (defaultCb) {
                        defaultCb(error);
                    }
            }
        }
    };

    return handleLoginError;
};

export default useLoginError;
